import { createReviewEvidence, criterionLabel } from '@mio/shared';

import type { ProjectArtifact, ReviewEvidence, WorkshopEvaluation } from '@mio/shared';

const evidenceRef = /(?<![\w./-])e[1-9]\d*(?:\s*[–—-]\s*e[1-9]\d*)?(?![\w/-]|\.[\w])/g;

function narrative(result: WorkshopEvaluation) {
  return [
    result.feedback,
    ...result.criteria.map((item) => item.feedback),
    ...result.knowledgeGaps.flatMap((item) =>
      'question' in item
        ? [
            item.concept,
            item.evidence,
            item.whyItMatters,
            item.question,
            item.nextStep,
            ...('missing' in item ? [item.missing] : []),
          ]
        : [item.concept, item.evidence],
    ),
    ...('version' in result ? result.strengths.map((item) => item.description) : []),
    ...('solutionNotes' in result && result.solutionNotes ? [result.solutionNotes] : []),
    ...result.nextSteps,
  ];
}

export function needsReviewReferenceFiles(result: WorkshopEvaluation) {
  return narrative(result).some((text) => new RegExp(evidenceRef).test(text));
}

/** Presentation only. Never rewrite frozen files, explanations, citation quotes or grades. */
export function presentWorkshopReview(
  result: WorkshopEvaluation,
  artifact?: ProjectArtifact,
  explanation = '',
): WorkshopEvaluation {
  const references = artifact ? createReviewEvidence(artifact, explanation).references : {};
  // A learner may genuinely name a variable e1 or discuss these strings. Keep
  // identifiers found in their evidence, even if they also resemble internal refs.
  const quotedEvidence = [
    ...result.criteria.flatMap((item) =>
      'citations' in item ? item.citations.map((citation) => citation.quote) : [],
    ),
    ...result.knowledgeGaps.flatMap((item) =>
      'citations' in item ? item.citations.map((citation) => citation.quote) : [],
    ),
    ...('version' in result
      ? result.strengths.flatMap((item) => item.citations.map((citation) => citation.quote))
      : []),
    ...(artifact?.files.flatMap((file) => [file.path, file.content]) ?? []),
    explanation,
  ];
  const learnerIdentifiers = new Set(
    quotedEvidence.flatMap((text) => [...text.matchAll(/\be[1-9]\d*\b/g)].map(([id]) => id)),
  );
  const location = (id: string) => {
    const ref = references[id];
    if (!ref) return null;
    return `${ref.kind === 'source' ? ref.path : 'submitted explanation'}, line ${ref.line}`;
  };
  const prose = (text: string) =>
    text
      // This audit-only sentence was observed in an otherwise valid saved review.
      // Its removal carries no judgment about the learner's submitted work.
      .replace(
        /(?:The )?draft review(?:['’]s)? ratings are accurate and are confirmed here\.?/gi,
        (match) => (quotedEvidence.some((text) => text.includes(match)) ? match : ''),
      )
      .replace(evidenceRef, (match) => {
        const [first, last] = match.split(/\s*[–—-]\s*/);
        if (!first || learnerIdentifiers.has(first) || (last && learnerIdentifiers.has(last)))
          return match;
        const start = location(first);
        if (!last) return start ?? 'submitted evidence';
        const end = location(last);
        const startRef = references[first];
        const endRef = references[last];
        if (
          !start ||
          !end ||
          !startRef ||
          !endRef ||
          Number(first.slice(1)) > Number(last.slice(1))
        )
          return 'submitted evidence';
        if (
          startRef.kind === endRef.kind &&
          (startRef.kind === 'explanation' ||
            (endRef.kind === 'source' && startRef.path === endRef.path))
        )
          return `${startRef.kind === 'source' ? startRef.path : 'submitted explanation'}, lines ${startRef.line}–${endRef.line}`;
        return `${start} through ${end}`;
      })
      .trim();
  return {
    ...result,
    feedback: prose(result.feedback),
    criteria: result.criteria.map((item) => ({ ...item, feedback: prose(item.feedback) })),
    knowledgeGaps: result.knowledgeGaps.map((item) => ({
      ...item,
      concept: prose(item.concept),
      evidence: prose(item.evidence),
      ...('question' in item
        ? {
            whyItMatters: prose(item.whyItMatters),
            question: prose(item.question),
            nextStep: prose(item.nextStep),
            ...('missing' in item ? { missing: prose(item.missing) } : {}),
          }
        : {}),
    })),
    ...('version' in result
      ? {
          strengths: result.strengths.map((item) => ({
            ...item,
            description: prose(item.description),
          })),
        }
      : {}),
    ...('solutionNotes' in result && result.solutionNotes
      ? { solutionNotes: prose(result.solutionNotes) }
      : {}),
    nextSteps: result.nextSteps.map(prose),
  };
}

export interface ReviewSectionItem {
  id: string;
  label: string;
  result: 'met' | 'partial' | 'not_met';
  required: boolean;
  /** Advisory criteria never block completion. */
  advisory: boolean;
  text: string;
  citations: ReviewEvidence[];
}
export interface ReviewUnderstandingGap {
  criterionId: string;
  label: string;
  concept: string;
  evidence: string;
  whyItMatters: string | null;
  question: string | null;
  citations: ReviewEvidence[];
}
export interface ReviewSections {
  outcome: 'passed' | 'revision_requested';
  /** Every criterion in rubric order, for the summary chips. */
  criteria: ReviewSectionItem[];
  /** Criteria the submission demonstrated. */
  completed: ReviewSectionItem[];
  /** Evidence-backed work exists, but the final criterion is not fully satisfied. */
  partialProgress: ReviewSectionItem[];
  /** Criteria still partial or absent, each with the concrete deliverable that is missing. */
  notDone: (ReviewSectionItem & { missing: string })[];
  understandingGaps: ReviewUnderstandingGap[];
  /** Ordered, de-duplicated actions: each gap's next step, then the review's next steps. */
  nextActions: string[];
  solutionNotes: string | null;
  required: { met: number; total: number } | null;
}

/** One reading order for every stored review format: completed, not done, gaps, next. */
export function reviewSections(result: WorkshopEvaluation): ReviewSections {
  const labels = new Map(
    result.criteria.map((criterion) => [
      criterion.id,
      criterionLabel({
        id: criterion.id,
        ...('label' in criterion && criterion.label ? { label: criterion.label } : {}),
      }),
    ]),
  );
  const gaps = new Map(
    result.knowledgeGaps.flatMap((gap) => ('criterionId' in gap ? [[gap.criterionId, gap]] : [])),
  );
  const items = result.criteria.map((criterion): ReviewSectionItem => {
    const required = 'required' in criterion ? criterion.required : true;
    return {
      id: criterion.id,
      label: labels.get(criterion.id) ?? criterion.id,
      result: criterion.result,
      required,
      advisory: !required,
      text: criterion.feedback,
      citations: 'citations' in criterion ? criterion.citations : [],
    };
  });
  const notDone = items
    .filter((item) => item.result !== 'met')
    .map((item) => {
      const gap = gaps.get(item.id);
      return {
        ...item,
        missing: gap && 'missing' in gap && gap.missing ? gap.missing : item.text,
      };
    });
  const understandingGaps = result.knowledgeGaps.map((gap): ReviewUnderstandingGap => ({
    criterionId: 'criterionId' in gap ? gap.criterionId : '',
    label: ('criterionId' in gap ? labels.get(gap.criterionId) : undefined) ?? gap.concept,
    concept: gap.concept,
    evidence: gap.evidence,
    whyItMatters: 'whyItMatters' in gap ? gap.whyItMatters : null,
    question: 'question' in gap ? gap.question : null,
    citations: 'citations' in gap ? gap.citations : [],
  }));
  const nextActions: string[] = [];
  for (const step of [
    ...result.knowledgeGaps.flatMap((gap) => ('nextStep' in gap ? [gap.nextStep] : [])),
    ...result.nextSteps,
  ]) {
    const text = step.trim();
    if (text && !nextActions.some((existing) => existing.toLowerCase() === text.toLowerCase()))
      nextActions.push(text);
  }
  return {
    outcome: result.verdict,
    criteria: items,
    completed: items.filter((item) => item.result === 'met'),
    partialProgress: items.filter((item) => item.result === 'partial'),
    notDone,
    understandingGaps,
    nextActions,
    solutionNotes: 'solutionNotes' in result ? (result.solutionNotes ?? null) : null,
    required:
      'metrics' in result
        ? { met: result.metrics.required.met, total: result.metrics.required.total }
        : null,
  };
}
